import { Button, Input, List, Select, Spin, notification } from "antd";
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import React, { useRef, useState } from "react";

import Modal from "antd/lib/modal/Modal";
import axios from "../../utils/axios";
import PropTypes from "prop-types";
import { getNoCache } from "../../utils/noCache";
import { useEffect } from "react";

function Vpn({ resourceAccess, username, search, onSearchChange }) {
  /* ---------------------------------- Hooks --------------------------------- */

  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedVpn, setSelectedVpn] = useState("elsan");
  const [trig, setTrig] = useState("");

  // Evite de renotifier toutes les 10 s tant que l'erreur persiste.
  const errorNotified = useRef(false);

  const getData = React.useCallback(async () => {
    let res;
    try {
      res = await getNoCache(axios, "/api/vpn");
    } catch (e) {
      res = null;
    }
    // Defensif : reponse non-tableau (HTML, endpoint indisponible) -> erreur explicite.
    if (!res || !Array.isArray(res.data)) {
      if (!errorNotified.current) {
        errorNotified.current = true;
        notification.error({
          message: "Erreur",
          description: "Impossible de charger la liste des VPN",
        });
      }
      setLoaded(true);
      return;
    }
    errorNotified.current = false;
    // Filtrage par roles fait cote serveur ; tri deja fait aussi, on garde un
    // filtre defensif sur les entrees invalides uniquement.
    setData(res.data.filter((con) => con && typeof con.name === "string"));
    setLoaded(true); // 1er chargement termine : on n'affiche plus le Spin meme si vide
  }, []);

  useEffect(() => {
    getData();
    const interval = setInterval(() => {
      getData();
    }, 10000);
    return () => clearInterval(interval);
  }, [getData]);

  /* ---------------------------------- Data ---------------------------------- */

  /* ----------------------------- Partial render ----------------------------- */

  const getAvatar = (item) => {
    if (item.state === "running") {
      return <CheckCircleFilled style={{ color: "green", fontSize: 18 }} />;
    } else if (item.state === "exited") {
      return <CloseCircleFilled style={{ color: "red", fontSize: 18 }} />;
    } else {
      return <LoadingOutlined />;
    }
  };

  /* -------------------------------- Handlers -------------------------------- */

  const notifyError = (e, fallback) => {
    const status = e && e.response && e.response.status;
    const msg = e && e.response && e.response.data && e.response.data.error;
    notification.error({
      message: "Erreur",
      description:
        msg && [400, 403, 409].includes(status) ? msg : fallback,
    });
  };

  const handleRunVpn = async (id, state) => {
    const action = state === "running" ? "stop" : "start";
    try {
      await axios.post(`/api/vpn/${encodeURIComponent(id)}/${action}`);
    } catch (e) {
      notifyError(e, "Action impossible sur ce VPN");
    }
    getData();
  };

  const handleShowModal = () => {
    setShowModal(!showModal);
  };

  const handleCreateContainer = async () => {
    setShowModal(false);
    try {
      // Le serveur controle les droits, cree ET demarre le conteneur.
      await axios.post("/api/vpn", { trig: trig.toLowerCase() });
    } catch (e) {
      notifyError(e, "Impossible de creer le VPN");
    }
    getData();
  };

  /* --------------------------------- Render --------------------------------- */

  return (
    <>
      <Modal
        open={showModal}
        onCancel={handleShowModal}
        onOk={handleCreateContainer}
        cancelText="Annuler"
        okText="Lancer"
        title="Créer un VPN"
      >
        <div className="form-row">
          <div className="form-label">Type :</div>
          <Select
            value={selectedVpn}
            onChange={(value) => setSelectedVpn(value)}
          >
            <Select.Option value="elsan">Elsan</Select.Option>
          </Select>
        </div>
        <div className="form-row">
          <div className="form-label">Utilisateur :</div>
          <Input
            placeholder="Trigramme"
            value={trig}
            onChange={(e) => setTrig(e.target.value)}
          />
        </div>
      </Modal>
      <div className="vpn-header">
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Recherche"
        />
        <Button onClick={handleShowModal} type="primary">
          Créer un VPN
        </Button>
      </div>
      {loaded ? (
        <div className="scrollable">
          <List
            itemLayout="horizontal"
            dataSource={data.filter((container) =>
              container.name
                .toLowerCase()
                .includes(search.toLowerCase())
            )}
            renderItem={(item) => {
              const isExploit = item.name.includes("(xp)");
              let style = { minWidth: 120 };
              if (isExploit) {
                style.background = "#f39c12";
                style.borderColor = "#f39c12";

                if (item.state === "running") {
                  style.background = "#ff4d4f";
                  style.borderColor = "#ff4d4f";
                }
              }
              return (
                <List.Item
                  actions={[
                    <Button
                      onClick={() => handleRunVpn(item.id, item.state)}
                      style={style}
                      type={item.state === "running" ? "danger" : "primary"}
                    >
                      {item.state === "exited" ? "Démarrer" : "Arrêter"}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={getAvatar(item)}
                    title={item.name.replace("(xp)", "")}
                    description={item.status}
                  />
                </List.Item>
              );
            }}
          />
        </div>
      ) : (
        <Spin
          style={{
            width: "100%",
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        />
      )}
    </>
  );
}

Vpn.propTypes = {
  search: PropTypes.string.isRequired,
  onSearchChange: PropTypes.func.isRequired,
};

export default Vpn;
